import { useEffect, useRef, useState } from 'react';

import { MessageBouncedError } from '../data/moderation.js';
import { track } from '../lib/analytics.js';
import { useVoiceRecorder } from './useVoiceRecorder.js';
import { micBlockedReason, type VoiceClip } from './voiceNote.js';

export interface VoiceContext {
  surface: 'chat' | 'widget' | 'pane';
  channelId: string;
}

/** Sends the clip and resolves with the message id. */
type SendVoice = (clip: VoiceClip) => Promise<string | undefined>;

const seconds = (ms: number) => Math.round(ms / 100) / 10;

/** The composer's voice cycle (record, review, then send or delete) and its funnel events. */
export function useVoiceDraft(context: VoiceContext | undefined, sendClip: SendVoice) {
  const [clip, setClip] = useState<VoiceClip | null>(null);
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  const contextRef = useRef(context);
  contextRef.current = context;

  const report = (
    event: string,
    properties: Record<string, unknown> = {},
    current = contextRef.current,
  ) => {
    if (!current) return;
    track(event, { surface: current.surface, channel_id: current.channelId, ...properties });
  };

  const recorder = useVoiceRecorder({
    onClip: (next, reason) => {
      setClip(next);
      report('voice_note_recording_stopped', {
        duration_s: seconds(next.durationMs),
        stop_reason: reason,
      });
    },
    onTooShort: (durationMs) =>
      report('voice_note_recording_stopped', {
        duration_s: seconds(durationMs),
        stop_reason: 'too_short',
      }),
    onBlocked: (errorName) =>
      report('voice_note_mic_blocked', {
        reason: micBlockedReason(errorName),
        error_name: errorName,
      }),
  });
  const recording = recorder.phase === 'recording';

  const unsent = useRef(clip);
  unsent.current = clip;
  useEffect(
    () => () => {
      if (unsent.current) URL.revokeObjectURL(unsent.current.url);
    },
    [],
  );

  const drop = () => {
    if (!unsent.current) return;
    URL.revokeObjectURL(unsent.current.url);
    setClip(null);
    setFailed(false);
  };

  const channelId = context?.channelId;
  const switched = useRef(channelId);
  useEffect(() => {
    if (switched.current === channelId) return;
    switched.current = channelId;
    recorder.cancel();
    drop();
  }, [channelId]);

  const start = () => {
    report('voice_note_mic_clicked', { mic_permission: recorder.permission() });
    void recorder.start();
  };

  const discard = () => {
    if (recording) {
      report('voice_note_recording_deleted', {
        stage: 'recording',
        duration_s: seconds(recorder.cancel()),
      });
      return;
    }
    if (!clip) return;
    report('voice_note_recording_deleted', {
      stage: 'review',
      duration_s: seconds(clip.durationMs),
    });
    drop();
  };

  const send = async () => {
    if (!clip || sending) return;
    const from = contextRef.current;
    setSending(true);
    setFailed(false);
    recorder.clearProblem();
    try {
      const messageId = await sendClip(clip);
      report(
        'voice_note_sent',
        {
          message_id: messageId,
          duration_s: seconds(clip.durationMs),
          mime_type: clip.blob.type,
          size_kb: Math.round(clip.blob.size / 1024),
        },
        from,
      );
      URL.revokeObjectURL(clip.url);
      setClip(null);
    } catch (error) {
      if (contextRef.current?.channelId !== from?.channelId) return;
      setFailed(!(error instanceof MessageBouncedError));
    } finally {
      setSending(false);
    }
  };

  return {
    recorder,
    clip,
    sending,
    failed,
    recording,
    busy: recording || clip !== null,
    start,
    discard,
    send,
  };
}
